import { aiHelper } from './ai-helper';

export const parseTextareaToHtml = (textareaContent?: string) => {
  // Convert literal \n strings (escaped newlines) to actual newlines first
  const normalizedContent = textareaContent?.replaceAll('\\n', '\n');
  const { beforeText, afterText, aiBlocks } = aiHelper(normalizedContent);
  if (aiBlocks?.length) {
    const beforeContent = beforeText?.replaceAll('\n', '<br>');
    const aftercontent = afterText?.replaceAll('\n', '<br>');
    return `<p>${beforeContent}</p><ai-personalized-block></ai-personalized-block><p>${aftercontent}</p>`;
  } else {
    const textareaContentWithBreaktag = normalizedContent?.replaceAll(
      '\n',
      '<br>'
    );
    return `<p>${textareaContentWithBreaktag}</p>`;
  }
};

export const parseHtmlToTextarea = (html?: string) => {
  const htmlWithNewLine = html?.replaceAll('<br>', '\n');
  const dom = new DOMParser()?.parseFromString(
    htmlWithNewLine,
    'text/html'
  )?.documentElement;
  const paragraphs = dom?.getElementsByTagName('p');
  const paragraphTexts: string[] = [];
  for (let i = 0; i < paragraphs.length; i++) {
    const paragraph = paragraphs[i];
    paragraphTexts.push(paragraph?.textContent || '');
  }
  return paragraphTexts.join('');
};

export const htmlHasFormatting = (htmlString?: string) => {
  const stylePatterns = [
    /<(b|strong)\b/i, // Bold
    /<(i|em)\b/i, // Italic
    /<u\b/i, // Underline
    /<(s|strike|del)\b/i, // Strikethrough
    /<h[1-4]\b/i, // Headings (h1-h4)
    /<(a)\b/i, // Links
    /<(ul|ol|li)\b/i, // Lists (unordered/ordered/items)
    /align\s*=\s*['"](left|right|center)['"]/i, // align attribute
    /style\s*=\s*['"][^'"]*text-align\s*:\s*(left|right|center)[^'"]*['"]/i, // inline style alignment
  ];

  return stylePatterns.some((pattern) => pattern.test(htmlString));
};

// Elements kula's editor and our email templates actually emit. A closed list,
// not a generic /<[a-z]+>/: a plain-text body that happens to contain "<3" or
// "a <b" must stay plain text.
const HTML_ELEMENT =
  /<\/?(?:p|br|div|span|blockquote|pre|code|hr|figure|figcaption|img|table|thead|tbody|tfoot|tr|td|th|col|colgroup|h[1-6]|b|strong|i|em|u|s|strike|del|a|ul|ol|li|dl|dt|dd)\b[^>]*>/i;

/**
 * Whether the content is already HTML — markup of any kind, not just styled
 * markup.
 *
 * `htmlHasFormatting` answers a different question ("would turning this into
 * plain text lose anything?") and deliberately looks only for bold/italic/links
 * and friends. Using it to decide whether something is HTML misses the most
 * common body of all: paragraphs with no styling on them.
 */
export const isHtmlContent = (content?: string | null) =>
  HTML_ELEMENT.test(content ?? '');

export const convertPlainTextToHtml = (content?: string | null) => {
  if (!content) return '<p></p>';

  // Already HTML: hand it back untouched. Testing for *formatting* here sent
  // every unstyled HTML body — `<p>a</p><p>b</p>`, which is what the editor
  // produces for plain paragraphs — down the plain-text path below, where it was
  // wrapped in one more `<p>`. The browser cannot nest paragraphs, so it closed
  // the outer one immediately and left an empty `<p>` at each end, which
  // `.editor-style p:empty::after` then renders as a blank line.
  if (isHtmlContent(content)) {
    return content;
  }

  // Split by newlines and wrap each line in a paragraph
  const lines = content.split('\n');
  const paragraphs = lines.map((line) => {
    // If line is empty, create an empty paragraph
    const trimmedLine = line.trim();
    return trimmedLine ? `<p>${line}</p>` : '<p></p>';
  });

  return paragraphs.join('');
};
